import { DEFAULT_CONFIG } from "./payrollConstants";

export function calcSalaryFull({ basic: totalSalaryInput, month, year, absent_days = 0, ot_hours = 0, cfg, emp_id, extra }) {
  const c = cfg || DEFAULT_CONFIG;
  const wd = c.working_days === -1 ? new Date(year, month, 0).getDate() : (c.working_days || 26);

  // Clean whole integer gross input — absolutely no floating point values in salary
  const totalSalary = Math.round(Number(totalSalaryInput) || 0);

  // Calculate Basic based on configured percentage (default 50%) — but enforce min wage floor on Basic+DA if configured
  const custom_basic_enabled = c.custom_basic_enabled;
  const globalBasicPct = c.basic_pct !== undefined ? (c.basic_pct / 100) : 0.5;
  const basicPct = (custom_basic_enabled && extra?.custom_basic_pct !== undefined && extra?.custom_basic_pct !== null && extra?.custom_basic_pct !== "")
    ? (parseFloat(extra.custom_basic_pct) / 100)
    : globalBasicPct;
  const rawBasic = Math.round(totalSalary * basicPct);
  const rawDa = c.da ? Math.round(rawBasic * (c.da_pct || 0) / 100) : 0;
  const minWageFloor = (c.min_wage_enabled && c.min_wage > 0) ? Math.round(c.min_wage) : 0;
  const basicPlusDaFloor = Math.max(rawBasic + rawDa, minWageFloor);
  const basic = c.da ? Math.round(basicPlusDaFloor / (1 + (c.da_pct || 0) / 100)) : basicPlusDaFloor;

  // Fixed allowances from config
  const da  = c.da ? Math.round(basic * (c.da_pct || 0) / 100) : 0;
  const lta = c.lta ? Math.round(basic * (c.lta_pct || 0) / 100) : 0;
  const conveyance = c.conveyance ? Math.round(c.conveyance_amount || 0) : 0;
  const medical    = c.medical    ? Math.round(c.medical_amount    || 0) : 0;
  const otherFixedAllowances = da + lta + conveyance + medical;

  // Statutory Bonus:
  // Under the Payment of Bonus Act, 1965, bonus is payable when (basic + da) <= 21,000
  const bonusMonths = c.bonus ? (c.bonus_months && c.bonus_months.length > 0 ? c.bonus_months : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]) : [];
  const isBonusMonth = c.bonus ? (c.bonus_frequency === "monthly" || bonusMonths.includes(month)) : false;
  const bonus = (isBonusMonth && (basic + da) <= 21000) ? Math.round(basic * (c.bonus_pct || 8.33) / 100) : 0;

  // Option 2: Configurable Bonus inclusion mode
  // bonus_in_gross === true: Bonus is part of agreed monthly Gross (CTC model, default).
  // bonus_in_gross === false: Bonus is paid over & above Gross (traditional payout model).
  const isBonusInGross = c.bonus_in_gross !== false;
  const bonusInGrossDeduction = (c.bonus && isBonusInGross) ? bonus : 0;

  // HRA based on company config
  const hra_type = c.hra_type || "pct";
  const default_hra_val = c.hra_value !== undefined ? c.hra_value : (c.hra_pct || 40);
  // When hra_customised is ON, use per-employee value (default 0 if not set)
  const hasCustomHra = extra?.custom_hra_pct !== undefined && extra?.custom_hra_pct !== null && extra?.custom_hra_pct !== "";
  const hra_raw = (c.hra_customised || hasCustomHra)
    ? (hasCustomHra ? Math.round(parseFloat(extra.custom_hra_pct) || 0) : 0)
    : (hra_type === "pct" ? Math.round(basic * default_hra_val / 100) : Math.round(default_hra_val || 0));
  // STATUTORY & BUDGET SQUEEZE:
  // Basic (+ DA) can NEVER go below minimum wage floor.
  // Non-statutory allowances (HRA) compress to fit within the remaining budget.
  const hra_budget = Math.max(0, totalSalary - basic - otherFixedAllowances - bonusInGrossDeduction);
  const hra = Math.min(hra_raw, hra_budget);
  const hra_was_capped = hra < hra_raw; // used for UI warning in sample payslip

  const special_config = c.special_allowance
    ? (c.special_allowance_type === "pct"
        ? Math.round(basic * (c.special_allowance_value || 0) / 100)
        : Math.round(c.special_allowance_value || 0))
    : 0;
  
  // Merge Others Earning into Special Allowance for DB storage
  const hasCustomSpecial = extra?.custom_special_allowance !== undefined && extra?.custom_special_allowance !== null && extra?.custom_special_allowance !== "";
  const custom_special = (c.custom_special_allowance_enabled || hasCustomSpecial) ? (hasCustomSpecial ? Math.round(parseFloat(extra.custom_special_allowance) || 0) : 0) : 0;
  const fixed_special = special_config + custom_special;

  // Remaining from gross (after Basic + fixed allowances + bonus + HRA + explicit special allowance) goes to Special / Other Allowance
  const others_earning = Math.max(0, Math.round(totalSalary - basic - otherFixedAllowances - bonusInGrossDeduction - hra - fixed_special));
  const special = Math.round(fixed_special + others_earning);
  const otBase = c.ot_basis === "gross" 
    ? (basic + hra + da + lta + special + conveyance + medical) 
    : (basic + da);
  const otRatePerHour = (otBase / (wd * 8)) * (c.ot_rate_multiplier || 1);
  const ot_pay = c.ot_applicable ? Math.round(ot_hours * otRatePerHour) : 0;

  // Absence deduction
  const isNoDeduct = !c.absence_deduction || c.absence_basis === "no_deduct" || c.absence_basis === "none";
  const deductionBase = c.absence_basis === "gross"
    ? totalSalary : basic;
  const per_day = Math.round(deductionBase / wd);
  const absence_deduction = !isNoDeduct ? Math.round(per_day * (absent_days || 0)) : 0;

  const gross = Math.max(0, Math.round(basic + hra + da + lta + special + conveyance + medical + bonus + ot_pay - absence_deduction));

  // PF + VPF
  let pensionApplicable = c.pension_applicable !== false;
  if (extra?.emp_dob) {
    const d = new Date(extra.emp_dob);
    if (!isNaN(d)) {
      const calcDate = new Date(year, month - 1, 1);
      let age = calcDate.getFullYear() - d.getFullYear();
      if (calcDate.getMonth() < d.getMonth() || (calcDate.getMonth() === d.getMonth() && calcDate.getDate() < d.getDate())) {
        age--;
      }
      if (age >= 58) pensionApplicable = false;
    }
  }

  const presence_ratio = Math.max(0, 1 - ((absent_days || 0) / wd));
  const earned_pf_wage_base = isNoDeduct
    ? (basic + da)
    : (c.absence_basis === "basic" 
        ? Math.max(0, (basic + da) - absence_deduction) 
        : Math.round((basic + da) * presence_ratio));

  const pfWage = c.pf_applicable ? (c.pf_wage_cap !== false ? Math.min(earned_pf_wage_base, 25000) : earned_pf_wage_base) : 0;
  const pf_emp  = c.pf_applicable ? Math.round(pfWage * 0.12) : 0;
  const pf_er_core = c.pf_applicable ? Math.round(pfWage * 0.12) : 0;
  // pf_admin = 1% of pfWage (0.5% EDLI + 0.5% admin charges), both capped at ₹25,000 ceiling
  // BUG FIX: Both components cap at 25,000 — previous formula double-counted when wage_cap=false
  const pf_admin_base = c.pf_applicable ? Math.min(pfWage, 25000) : 0;
  const pf_admin = c.pf_applicable ? Math.round(pf_admin_base * 0.005) + Math.round(pf_admin_base * 0.005) : 0;
  const pf_er   = c.pf_applicable ? pf_er_core + pf_admin : 0;                      // Total employer = 13% (12% + 1% admin)
  const eps     = (c.pf_applicable && pensionApplicable) ? Math.round(Math.min(pfWage, 25000) * 0.0833) : 0;   // EPS capped at ₹25,000
  const epf_er  = c.pf_applicable ? Math.max(0, pf_er_core - eps) : 0;                       // EPF = remainder of 12%
  const vpf     = (extra?.vpf_enabled && extra?.vpf_pct > 0) ? Math.round(Number(extra.vpf_pct) || 0) : 0;

  // ESI - Under New Wage Code, ESI is calculated on Basic + DA (since allowances don't exceed 50% of Gross)
  const esi_rate_wage = basic + da; // Eligibility is based on Rate of Wage
  let esi_forced = false;
  if (extra?.esi_forced_until) {
    const [fYear, fMonth] = extra.esi_forced_until.split("-").map(Number);
    if (fYear > year || (fYear === year && fMonth >= month)) {
      esi_forced = true;
    }
  }
  const esi_eligible = c.esi_applicable && (esi_rate_wage <= 21000 || esi_forced);
  const esi_wage = earned_pf_wage_base; // Amount is calculated on Earned Wage
  const esi_emp = esi_eligible ? Math.ceil(esi_wage * 0.0075) : 0;
  const esi_er  = esi_eligible ? Math.ceil(esi_wage * 0.0325) : 0;

  // PT
  const pt = gross >= 25000 ? (month === 2 ? 300 : 200) : 0;

  // TDS — always use per-employee amount if set; else company-level flag
  const tds = (extra?.tds_amount || 0) > 0 ? Math.round(Number(extra.tds_amount) || 0) : 0;

  // LWF — Karnataka Labour Welfare Fund (₹50 employee, December only, as per 2025 amendment)
  const lwf = (c.lwf_applicable && month === 12) ? 50 : 0;

  const total_deductions = Math.round(pf_emp + vpf + esi_emp + pt + tds + lwf + absence_deduction);
  const net = Math.max(0, Math.round(gross - pf_emp - vpf - esi_emp - pt - tds - lwf));
  const gratuity = c.gratuity ? Math.round(basic * 0.0481) : 0;
  const ctc = Math.round(gross + pf_er + esi_er + gratuity);

  return {
    basic: Math.round(basic),
    hra: Math.round(hra),
    hra_raw: Math.round(hra_raw),
    hra_was_capped,
    da: Math.round(da),
    lta: Math.round(lta),
    special: Math.round(special),
    conveyance: Math.round(conveyance),
    medical: Math.round(medical),
    bonus: Math.round(bonus),
    bonus_in_gross: isBonusInGross,
    ot_pay: Math.round(ot_pay),
    per_day: Math.round(per_day),
    absence_deduction: Math.round(absence_deduction),
    gross: Math.round(gross),
    pfWage: Math.round(pfWage),
    pf_emp: Math.round(pf_emp),
    eps: Math.round(eps),
    epf_er: Math.round(epf_er),
    pf_er: Math.round(pf_er),
    vpf: Math.round(vpf),
    esi_emp: Math.round(esi_emp),
    esi_er: Math.round(esi_er),
    pt: Math.round(pt),
    tds: Math.round(tds),
    lwf: Math.round(lwf),
    total_deductions: Math.round(total_deductions),
    net: Math.round(net),
    ctc: Math.round(ctc),
    gratuity: Math.round(gratuity),
    _inputs: { basic: totalSalary, absent_days, ot_hours },
  };
}

// ── Helpers
export const fmtINR = v => "₹"+Math.round(parseFloat(v)||0).toLocaleString("en-IN",{minimumFractionDigits:0,maximumFractionDigits:0});
export const fmtNum = v => Math.round(parseFloat(v)||0).toLocaleString("en-IN",{minimumFractionDigits:0,maximumFractionDigits:0});
export const formatDateDDMMYYYY = (dStr) => {
  if (!dStr) return "";
  const clean = String(dStr).split("T")[0].trim();
  // YYYY-MM-DD
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(clean)) {
    const [y, m, d] = clean.split("-");
    return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  }
  // YYYY/MM/DD
  if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(clean)) {
    const [y, m, d] = clean.split("/");
    return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  }
  // DD-MM-YYYY
  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(clean)) {
    const [d, m, y] = clean.split("-");
    return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  }
  // DD/MM/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(clean)) {
    const [d, m, y] = clean.split("/");
    return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
  }
  // 8-digit DDMMYYYY
  if (/^\d{8}$/.test(clean)) {
    const d = clean.slice(0, 2);
    const m = clean.slice(2, 4);
    const y = clean.slice(4, 8);
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(dStr);
  if (!isNaN(dt.getTime())) {
    return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  }
  return clean;
};

export function parseDateFlexible(dStr) {
  if (!dStr) return null;
  const clean = String(dStr).split("T")[0].trim();
  let d, m, y;
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(clean)) {
    [y, m, d] = clean.split("-");
  } else if (/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(clean)) {
    [y, m, d] = clean.split("/");
  } else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(clean)) {
    [d, m, y] = clean.split("-");
  } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(clean)) {
    [d, m, y] = clean.split("/");
  } else if (/^\d{8}$/.test(clean)) {
    d = clean.slice(0, 2);
    m = clean.slice(2, 4);
    y = clean.slice(4, 8);
  } else {
    const dt = new Date(dStr);
    return isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  return isNaN(dt.getTime()) ? null : dt;
}

export const fmtDate = d => {
  if(!d) return "—";
  return formatDateDDMMYYYY(d) || "—";
};
export const fmtDDMMYYYY = d => formatDateDDMMYYYY(d);

export const parseDDMMYYYYToISO = val => {
  if (!val) return "";
  const str = String(val).trim();
  let d, m, y;
  if (/^\d{8}$/.test(str)) {
    d = parseInt(str.slice(0, 2), 10);
    m = parseInt(str.slice(2, 4), 10);
    y = parseInt(str.slice(4, 8), 10);
  } else {
    const parts = str.split(/[/.-]/);
    if (parts.length === 3) {
      d = parseInt(parts[0], 10);
      m = parseInt(parts[1], 10);
      y = parseInt(parts[2], 10);
    }
  }
  if (!isNaN(d) && !isNaN(m) && !isNaN(y) && m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
    const daysInMonth = new Date(y, m, 0).getDate();
    if (d <= daysInMonth) {
      return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }
  }
  return "";
};
export function getEmployeeMonthStatus(e, month, year, wd) {
  let eligible = true;
  let lwd_absent = 0;

  // 1. Check Date of Exit
  if (e.date_of_exit) {
    const dExit = new Date(e.date_of_exit);
    if (!isNaN(dExit)) {
      const exitY = dExit.getFullYear();
      const exitM = dExit.getMonth() + 1;
      
      // If exit was before this month, they are not eligible
      if (exitY < year || (exitY === year && exitM < month)) {
        return { eligible: false, lwd_absent: 0 };
      }
      
      // If exit is in this month, add absent days for the end of the month
      if (exitY === year && exitM === month) {
        const lwdDay = dExit.getDate();
        lwd_absent += Math.max(0, wd - lwdDay);
      }
    }
  }

  // 2. Check Date of Joining
  if (e.doj) {
    const dJoin = new Date(e.doj);
    if (!isNaN(dJoin)) {
      const joinY = dJoin.getFullYear();
      const joinM = dJoin.getMonth() + 1;
      
      // If joining is after this month, they are not eligible yet
      if (joinY > year || (joinY === year && joinM > month)) {
        return { eligible: false, lwd_absent: 0 };
      }
      
      // If joining is in this month, add absent days for the beginning of the month
      if (joinY === year && joinM === month) {
        const dojDay = dJoin.getDate();
        lwd_absent += Math.max(0, dojDay - 1);
      }
    }
  }

  // Cap absent days at the total working days
  lwd_absent = Math.min(lwd_absent, wd);

  return { eligible, lwd_absent };
}
export function validatePAN(pan) { if(!pan||typeof pan!=="string") return false; return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan.trim().toUpperCase()); }
export function calcAge(dob) {
  if (!dob) return "";
  const birthDate = parseDateFlexible(dob);
  if (!birthDate) return "";
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}
export function validateDOJ(doj) {
  const val = (doj||"").trim();
  if (!val) return { ok:false, msg:"Date of joining is required." };
  const d = parseDateFlexible(val);
  if (!d) return { ok:false, msg:"Invalid date format." };
  const now = new Date(); now.setHours(23,59,59,999);
  if (d.getFullYear() < 1980) return { ok:false, msg:"Year must be 1980 or later." };
  if (d > now) return { ok:false, msg:"Date of joining cannot be in the future." };
  return { ok:true, msg:"" };
}

// Helper to extract employee identifier string for clean natural sorting
export function getEmpIdCode(emp) {
  return (emp?.employee_code ?? emp?.emp_code ?? emp?.code ?? emp?.emp_id ?? emp?.employee_id ?? emp?.id ?? "").toString().trim();
}

// Natural numeric comparator for Employee IDs (e.g. EMP-2 before EMP-10)
export function cmpEmpId(empA, empB) {
  const codeA = getEmpIdCode(empA);
  const codeB = getEmpIdCode(empB);
  if (!codeA && !codeB) {
    const nameA = (empA?.name || empA?.emp_name || "").toString().trim();
    const nameB = (empB?.name || empB?.emp_name || "").toString().trim();
    return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
  }
  if (!codeA) return 1;
  if (!codeB) return -1;
  const cmp = codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: "base" });
  if (cmp !== 0) return cmp;
  const nameA = (empA?.name || empA?.emp_name || "").toString().trim();
  const nameB = (empB?.name || empB?.emp_name || "").toString().trim();
  return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
}

// Comparator for Employee Name with fallback to Emp ID
export function cmpEmpName(empA, empB) {
  const nameA = (empA?.name || empA?.emp_name || "").toString().trim();
  const nameB = (empB?.name || empB?.emp_name || "").toString().trim();
  const cmp = nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
  if (cmp !== 0) return cmp;
  return cmpEmpId(empA, empB);
}

